import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX, type Ref } from "react"
import { DockviewReact, themeLightSpaced, type DockviewApi, type DockviewReadyEvent, type IWatermarkPanelProps, type IDockviewPanelProps, type SerializedDockview, IDockviewHeaderActionsProps, type IDockviewPanelHeaderProps } from "dockview"
import { get, set, isEmpty } from "lodash"
import { closeOverflowPopover, useOverflowPanelDrag } from "./overflow-panel-drag"
import Loading from "components/loading"
import CustomTabComponent from "./custom-tab"
import RightControls from "./right-controls"
import DefaultWatermark from "./watermark"
import { applyPanelDefinitions, applyPanelChanges } from "./utils"
import { clearPendingFloatingPositions, subscribePendingFloatingPositionCleanup } from "./dock-floating-for-maximize"
import { stripPanelIdsFromLayout, ensureGridRoot } from "connected-components/dashboard/helpers/prune-layout-panels"
import { getPanelOrderFromLayout } from "connected-components/dashboard/helpers/layout-order"
import { generateStablePanels, limitComponentsByMaxPanels } from "./template-utils"
import { DEFAULT_TEMPLATE } from "./templates"
import type { OpLayoutEngineRendererActions, OpLayoutEngineRendererProps, OPDockviewPersistence, OPDockviewPanelDefinition, OPLayoutPanel, OPLayoutPanelTabMetadata } from "./types"

import "dockview/dist/styles/dockview.css"
import "./style.scss"

function useLayoutActions(
  persistence: OPDockviewPersistence | undefined,
  panels: readonly OPDockviewPanelDefinition[],
  getExcludedPanelIds: () => Set<string>,
) {
  return useMemo(() => {
    const persistLayout = (api: DockviewApi) => {
      if (!persistence) {
        return
      }

      const rawSnapshot = api.toJSON()
      const excludedPanelIds = getExcludedPanelIds()
      const snapshot = excludedPanelIds.size > 0
        ? stripPanelIdsFromLayout(rawSnapshot, excludedPanelIds)
        : rawSnapshot

      if (!snapshot) {
        if (excludedPanelIds.size > 0) {
          // All non-transient panels were removed while transients remain. Persist an
          // empty layout so the previous panel arrangement is not restored on reload.
          persistence.save({
            ...rawSnapshot,
            panels: {},
            grid: ensureGridRoot({ ...rawSnapshot.grid, root: null }),
            floatingGroups: undefined,
          })
        }
        return
      }

      persistence.save(snapshot)
    }

    const applyDefaults = (api: DockviewApi) => {
      applyPanelDefinitions(api, panels)
      persistLayout(api)
    }

    const resetLayout = (api: DockviewApi) => {
      persistence?.clear()
      applyDefaults(api)
    }

    const loadLayout = (api: DockviewApi) => {
      if (!persistence) {
        applyDefaults(api)
        return
      }

      if (persistence.loading) {
        return
      }

      let serialized: SerializedDockview | null | undefined
      try {
        serialized = persistence.load()
      } catch {
        applyDefaults(api)
        return
      }

      if (!serialized || isEmpty(serialized)) {
        applyDefaults(api)
        return
      }

      try {
        api.fromJSON(serialized)
      } catch {
        persistence.clear()
        applyDefaults(api)
      }
    }

    return { resetLayout, loadLayout, persistLayout, loading: persistence?.loading ?? false }
  }, [persistence, panels, getExcludedPanelIds])
}

// Dockview expects component ids to be unique per renderer; we map each panel id to the renderer it should use.
interface ResolvedComponents {
  definitions: OPDockviewPanelDefinition[]
  registry: Record<string, (props: IDockviewPanelProps) => JSX.Element>
}

/**
 * - Dockview requires that every AddPanelOptions.component key maps to a renderer function in the components registry. This function produces:
 *   1) a registry of component ids -> renderer functions (This is where we need to create unique component ids)
 *   2) a rewritten set of panel definitions whose `component` fields reference those registry keys
 *
 * Why not a single component registry only?
 * - The registry only describes "how to render". We still need the panel definitions to describe
 *   "what and where" (ids, positions, sizes). Both are required to build the layout.
 *
 * How will this work:
 * - We first index panelId -> renderer from the calculated panels (these contain the actual renderers, we pass these renderers to the engine).
 * - For each template, we derive a registry id. If multiple definitions request the same component id, we suffix with `__<n>` to keep each registry entry unique.
 * - We then rewrite the definition.component to the unique registry id, and expose both the rewritten
 *   definitions and the registry for Dockview
 */
function resolveComponentDefinitions(
  panels: readonly OPLayoutPanel[],
  definitions: readonly OPDockviewPanelDefinition[],
): ResolvedComponents {
  const rendererByPanelId: Record<string, (props: IDockviewPanelProps) => JSX.Element> = {}
  for (const panel of panels) {
    rendererByPanelId[panel.panelId] = panel.component.renderer
  }

  const requestedIdUsage: Record<string, number> = {}
  const componentToRendererRegistry: Record<string, (props: IDockviewPanelProps) => JSX.Element> = {}

  const componentDefinitions = definitions.map(def => {
    const requestedComponentId = def.component
    const count = get(requestedIdUsage, [requestedComponentId], 0)
    set(requestedIdUsage, [requestedComponentId], count + 1)

    const registryComponentId = count === 0
      ? requestedComponentId
      : `${requestedComponentId}__${count}`

    componentToRendererRegistry[registryComponentId] = rendererByPanelId[def.id]

    return { ...def, component: registryComponentId }
  })

  return { definitions: componentDefinitions, registry: componentToRendererRegistry }
}

function assignLayoutRef(ref: Ref<OpLayoutEngineRendererActions | null>, value: OpLayoutEngineRendererActions | null): void {
  if (typeof ref === "function") {
    ref(value)
    return
  }

  if (ref && "current" in ref) {
    ref.current = value
  }
}

function getInitiallyMissingPanelIds(
  definitionIds: Set<string>,
  loadedIds: Set<string>,
): Set<string> {
  return new Set(Array.from(definitionIds).filter((definitionId) => !loadedIds.has(definitionId)))
}

export default function OpLayoutEngineRenderer({
  components,
  template,
  persistence,
  className,
  layoutRef,
  onLayoutReady,
  removePanel,
}: OpLayoutEngineRendererProps): JSX.Element {
  const apiRef = useRef<DockviewApi | null>(null)
  const [dockviewApiForOverflow, setDockviewApiForOverflow] = useState<DockviewApi | null>(null)
  const layoutChangeDisposableRef = useRef<{ dispose: () => void } | null>(null)
  const pendingFloatingCleanupDisposableRef = useRef<{ dispose: () => void } | null>(null)
  const overlayDisposableRef = useRef<{ dispose: () => void } | null>(null)
  const transientPanelIdsRef = useRef<Set<string>>(new Set())

  const { dockviewHostRef } = useOverflowPanelDrag(dockviewApiForOverflow)
  const prevPanelIdsRef = useRef<Set<string>>(new Set())
  const initialMissingPanelIdsRef = useRef<Set<string>>(new Set())
  const isInitialLoadRef = useRef(true)
  const hasLoadedLayoutRef = useRef(false)

  const templateFn = template || DEFAULT_TEMPLATE

  const limitedComponents = useMemo(
    () => limitComponentsByMaxPanels(components, templateFn.maxPanels),
    [components, templateFn.maxPanels],
  )

  const panels = useMemo<OPLayoutPanel[]>(
    () => generateStablePanels(limitedComponents),
    [limitedComponents],
  )

  // Create a mapping from panel IDs to their metadata
  const panelMetadata = useMemo(() => {
    const metadata: Record<string, OPLayoutPanelTabMetadata> = {}
    panels.forEach((panel) => {
      metadata[panel.panelId] = {
        removable: panel.component.removable ?? false,
        removePanel: panel.component.removePanel,
        removeRequiresConfirmation: panel.component.removeRequiresConfirmation ?? false,
        editable: panel.component.editable ?? false,
        onUpdateTitle: panel.component.onUpdateTitle,
        duplicatePanel: panel.component.duplicatePanel,
        icon: panel.component.icon,
        transient: panel.component.transient,
      }
    })
    return metadata
  }, [panels])

  useLayoutEffect(() => {
    transientPanelIdsRef.current = new Set(
      Object.entries(panelMetadata)
        .filter(([, meta]) => meta.transient)
        .map(([panelId]) => panelId),
    )
  }, [panelMetadata])

  const getExcludedPanelIds = useCallback(() => transientPanelIdsRef.current, [])

  const rawPanelDefinitions = useMemo<OPDockviewPanelDefinition[]>(
    () => templateFn.compile(panels),
    [templateFn, panels],
  )

  const { definitions: panelDefinitions, registry: componentRegistry } = useMemo(
    () => resolveComponentDefinitions(panels, rawPanelDefinitions),
    [panels, rawPanelDefinitions],
  )

  const { resetLayout, loadLayout, persistLayout, loading } = useLayoutActions(
    persistence,
    panelDefinitions,
    getExcludedPanelIds,
  )

  const handleReady = useCallback((event: DockviewReadyEvent) => {
    if (layoutChangeDisposableRef.current) {
      layoutChangeDisposableRef.current.dispose()
      layoutChangeDisposableRef.current = null
    }
    if (pendingFloatingCleanupDisposableRef.current) {
      pendingFloatingCleanupDisposableRef.current.dispose()
      pendingFloatingCleanupDisposableRef.current = null
    }
    if (overlayDisposableRef.current) {
      overlayDisposableRef.current.dispose()
      overlayDisposableRef.current = null
    }

    clearPendingFloatingPositions()

    apiRef.current = event.api
    setDockviewApiForOverflow(event.api)
    layoutChangeDisposableRef.current = event.api.onDidLayoutChange(() => {
      closeOverflowPopover()
      persistLayout(event.api)
    })
    pendingFloatingCleanupDisposableRef.current = subscribePendingFloatingPositionCleanup(event.api)

    overlayDisposableRef.current = event.api.onWillShowOverlay((overlayEvent) => {
      if (overlayEvent.panel != null && transientPanelIdsRef.current.has(overlayEvent.panel.id)) {
        overlayEvent.preventDefault()
      }
    })

    if (!persistence?.loading) {
      loadLayout(event.api)
      hasLoadedLayoutRef.current = true
    } else {
      hasLoadedLayoutRef.current = false
    }

    // Track actual panel IDs after loading to keep incremental updates in sync
    const initialIds = new Set(event.api.panels.map(panel => panel.id))
    const definitionIds = new Set(panelDefinitions.map(panel => panel.id))
    const initiallyMissingIds = getInitiallyMissingPanelIds(definitionIds, initialIds)

    prevPanelIdsRef.current = initialIds
    initialMissingPanelIdsRef.current = initiallyMissingIds
    isInitialLoadRef.current = false
  }, [loadLayout, persistLayout, persistence, panelDefinitions])

  useEffect(() => {
    return () => {
      apiRef.current = null
      layoutChangeDisposableRef.current?.dispose()
      layoutChangeDisposableRef.current = null
      pendingFloatingCleanupDisposableRef.current?.dispose()
      pendingFloatingCleanupDisposableRef.current = null
      overlayDisposableRef.current?.dispose()
      overlayDisposableRef.current = null
      clearPendingFloatingPositions()
    }
  }, [])

  // Apply layout again when persistence changes and is not loading
  useEffect(() => {
    if (hasLoadedLayoutRef.current) {
      return
    }

    const api = apiRef.current
    if (!api || !persistence || persistence.loading) {
      return
    }

    loadLayout(api)
    hasLoadedLayoutRef.current = true
    // Sync tracked panel IDs with actual layout after load
    const loadedIds = new Set(api.panels.map(panel => panel.id))
    const definitionIds = new Set(panelDefinitions.map(panel => panel.id))
    const initiallyMissingIds = getInitiallyMissingPanelIds(definitionIds, loadedIds)

    prevPanelIdsRef.current = loadedIds
    initialMissingPanelIdsRef.current = initiallyMissingIds
  }, [persistence, loadLayout, panelDefinitions])

  // Watch for panel changes after initial load and apply incrementally
  useEffect(() => {
    const api = apiRef.current
    if (!api || isInitialLoadRef.current) return

    const currentIds = new Set(panelDefinitions.map(p => p.id))
    const prevIds = prevPanelIdsRef.current

    // Detect changes
    const addedIds = Array.from(currentIds).filter(id => !prevIds.has(id))
    const removedIds = Array.from(prevIds).filter(id => !currentIds.has(id))

    if (addedIds.length === 0 && removedIds.length === 0) {
      return // No changes
    }

    // Find full definitions for added panels
    const missingAtInitialLoad = initialMissingPanelIdsRef.current
    const newAddedIds = addedIds.filter(id => !missingAtInitialLoad.has(id))
    const added = panelDefinitions.filter(p => newAddedIds.includes(p.id))

    applyPanelChanges(api, { added, removed: removedIds })

    prevPanelIdsRef.current = currentIds
    persistLayout(api)
  }, [panelDefinitions, persistLayout])

  // Sync panel titles when component definitions change
  useEffect(() => {
    const api = apiRef.current
    if (!api || isInitialLoadRef.current) return

    // Update titles for existing panels
    panels.forEach((panel) => {
      const dockviewPanel = api.getPanel(panel.panelId)
      if (dockviewPanel && dockviewPanel.api.title !== panel.component.title) {
        dockviewPanel.api.setTitle(panel.component.title)
      }
    })
  }, [panels])

  const actions = useMemo<OpLayoutEngineRendererActions>(() => ({
    resetLayout: api => resetLayout(api),
    getApi: () => apiRef.current,
    getPanelOrder: () => getPanelOrderFromLayout(apiRef.current?.toJSON?.() ?? null),
    focusPanel: (panelId) => apiRef.current?.getPanel(panelId)?.focus(),
    isPanelMounted: (panelId) => Boolean(apiRef.current?.getPanel(panelId)),
    ensurePanel: (panelId) => {
      const api = apiRef.current
      if (!api || api.getPanel(panelId)) {
        return Boolean(api?.getPanel(panelId))
      }
      const panelDef = panelDefinitions.find(panel => panel.id === panelId)
      if (!panelDef) {
        return false
      }
      applyPanelChanges(api, { added: [panelDef], removed: [] })
      return Boolean(api.getPanel(panelId))
    },
  }), [resetLayout, panelDefinitions])

  const WatermarkComponentWrapper = useMemo(() => {
    function WatermarkComponent(props: IWatermarkPanelProps) {
      return <DefaultWatermark {...props} onReset={resetLayout} />
    }
    return WatermarkComponent
  }, [resetLayout])

  const RightControlsComponent = useCallback((props: IDockviewHeaderActionsProps) => {
    const panelId = props.activePanel?.id ?? props.panels[0]?.id
    const metadata = panelId != null ? panelMetadata[panelId] : undefined
    return (
      <RightControls
        {...props}
        persistLayout={() => persistLayout(props.containerApi)}
        closePanel={metadata?.removable ? metadata?.removePanel : null}
        removeRequiresConfirmation={metadata?.removeRequiresConfirmation}
        disableDocking={metadata?.transient}
      />
    )
  }, [persistLayout, panelMetadata])

  const CustomTabComponentWithMetadata = useCallback((props: IDockviewPanelHeaderProps) => {
    const metadata = panelMetadata[props.api.id]
    return (
      <CustomTabComponent
        {...props}
        removable={metadata?.removable}
        removePanel={removePanel ?? metadata?.removePanel}
        removeRequiresConfirmation={metadata?.removeRequiresConfirmation}
        editable={metadata?.editable}
        onUpdateTitle={metadata?.onUpdateTitle}
        duplicatePanel={metadata?.duplicatePanel}
        icon={metadata?.icon}
      />
    )
  }, [panelMetadata, removePanel])

  // Here we are assigning the actions to the layout ref when it is mounted, and cleaning up when it is unmounted
  useEffect(() => {
    assignLayoutRef(layoutRef, actions)
    onLayoutReady?.(actions)

    return () => {
      assignLayoutRef(layoutRef, null)
      onLayoutReady?.(null)
    }
  }, [layoutRef, onLayoutReady, actions])

  if (loading) {
    return <Loading />
  }

  return (
    <DockviewReact
      ref={dockviewHostRef}
      onReady={handleReady}
      components={componentRegistry}
      defaultTabComponent={CustomTabComponentWithMetadata}
      rightHeaderActionsComponent={RightControlsComponent}
      watermarkComponent={WatermarkComponentWrapper}
      theme={themeLightSpaced}
      className={className}
      floatingGroupBounds="boundedWithinViewport"
    />
  )
}
